import { DOCUMENT } from '@angular/common';
import { computed, effect, inject, Injectable, signal } from '@angular/core';
import {
  ColorScheme,
  DEFAULT_THEME_CONFIG,
  Density,
  NB_THEME_CONFIG,
  TextSize,
  ThemeConfig,
} from './theme.types';

/**
 * Drives the design-system theme by reflecting signal state onto attributes of
 * the document root (`<html>`): `data-theme`, `data-text-size`, `data-density`.
 * The CSS in themes.css keys off those attributes — components never branch on
 * the mode themselves. Choices persist to localStorage (configurable).
 *
 * Provided in root; configure initial state with {@link provideTheme}.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly doc = inject(DOCUMENT);
  private readonly config: ThemeConfig = { ...DEFAULT_THEME_CONFIG, ...(inject(NB_THEME_CONFIG, { optional: true }) ?? {}) };

  private readonly _scheme = signal<ColorScheme>(this.config.scheme);
  private readonly _textSize = signal<TextSize>(this.config.textSize);
  private readonly _density = signal<Density>(this.config.density);
  private readonly _systemDark = signal<boolean>(this.prefersDark());
  // ── Accessibility axes (Phase M9), orthogonal to scheme/textSize/density ──
  private readonly _reducedMotion = signal<boolean>(this.prefersReducedMotion());

  /** The chosen scheme (`light` | `dark` | `system`). */
  readonly scheme = this._scheme.asReadonly();
  /** The chosen text size. */
  readonly textSize = this._textSize.asReadonly();
  /** The chosen density. */
  readonly density = this._density.asReadonly();
  /** Whether to suppress non-essential motion (defaults from `prefers-reduced-motion`). */
  readonly reducedMotion = this._reducedMotion.asReadonly();
  /** High-contrast mode. */
  /** Read-aloud (TTS of oppslag) opt-in. */
  /** Whether dark is *effectively* active (resolves `system` against the OS). */
  readonly isDark = computed(() => (this._scheme() === 'system' ? this._systemDark() : this._scheme() === 'dark'));

  constructor() {
    this.restore();
    this.watchSystem();

    const root = this.doc.documentElement;
    effect(() => {
      this.setAttr(root, 'data-theme', this.isDark() ? 'dark' : null);
    });
    effect(() => {
      this.setAttr(root, 'data-text-size', this._textSize() === 'large' ? 'large' : null);
    });
    effect(() => {
      this.setAttr(root, 'data-density', this._density() === 'simplified' ? 'simplified' : null);
    });
    effect(() => {
      this.setAttr(root, 'data-reduced-motion', this._reducedMotion() ? 'reduce' : null);
    });
    effect(() => this.persist());
  }

  setScheme(scheme: ColorScheme): void {
    this._scheme.set(scheme);
  }
  /** Flip between explicit light/dark (resolving `system` to its current effective value first). */
  toggleDark(): void {
    this._scheme.set(this.isDark() ? 'light' : 'dark');
  }
  toggleLargeText(): void {
    this._textSize.update((s) => (s === 'large' ? 'normal' : 'large'));
  }
  toggleSimplified(): void {
    this._density.update((d) => (d === 'simplified' ? 'normal' : 'simplified'));
  }

  // ---- internals ----

  private setAttr(el: Element, attr: string, value: string | null): void {
    if (value === null) el.removeAttribute(attr);
    else el.setAttribute(attr, value);
  }

  private prefersDark(): boolean {
    const w = this.doc.defaultView;
    return !!w?.matchMedia?.('(prefers-color-scheme: dark)').matches;
  }

  private prefersReducedMotion(): boolean {
    const w = this.doc.defaultView;
    return !!w?.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  }

  private watchSystem(): void {
    const mql = this.doc.defaultView?.matchMedia?.('(prefers-color-scheme: dark)');
    mql?.addEventListener?.('change', (e) => this._systemDark.set(e.matches));
  }

  private storage(): Storage | null {
    if (this.config.storageKey === false) return null;
    try {
      return this.doc.defaultView?.localStorage ?? null;
    } catch {
      return null; // privacy mode / blocked
    }
  }

  private restore(): void {
    const key = this.config.storageKey;
    if (key === false) return;
    const raw = this.storage()?.getItem(key);
    if (!raw) return;
    try {
      const v = JSON.parse(raw) as Partial<{
        scheme: ColorScheme;
        textSize: TextSize;
        density: Density;
        reducedMotion: boolean;
      }>;
      if (v.scheme) this._scheme.set(v.scheme);
      if (v.textSize) this._textSize.set(v.textSize);
      if (v.density) this._density.set(v.density);
      if (typeof v.reducedMotion === 'boolean') this._reducedMotion.set(v.reducedMotion);
    } catch {
      /* ignore corrupt value */
    }
  }

  private persist(): void {
    const key = this.config.storageKey;
    if (key === false) return;
    this.storage()?.setItem(
      key,
      JSON.stringify({
        scheme: this._scheme(),
        textSize: this._textSize(),
        density: this._density(),
        reducedMotion: this._reducedMotion(),
      }),
    );
  }
}
